import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslate, useNotify, usePermissions } from 'react-admin';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    Alert,
    Chip,
    MenuItem,
    FormControl,
    FormControlLabel,
    InputLabel,
    Select,
    Switch,
    TextField,
    CircularProgress,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { CreditCard as CreditCardIcon } from '@mui/icons-material';
import { apiClient, type ApiError } from '../../utils/apiClient';
import { API_CONFIG } from '../../config/api';
import { hasPermission } from '../../utils/hasPermission';

export type SoepayEnvironment = 'uat' | 'prod';

export interface SoepayAccount {
    siteId: string;
    environment: SoepayEnvironment;
    organizeId: string;
    appId: string;
    terminalTid: string;
    active: boolean;
    apiKeySet: boolean;
    apiTokenSet: boolean;
    updatedAt: string;
    updatedBy: string;
}

export interface SoepayAccountForm {
    environment: SoepayEnvironment | '';
    organizeId: string;
    appId: string;
    terminalTid: string;
    active: boolean;
    apiKey: string;
    apiToken: string;
}

export interface SoepayAccountBody {
    environment: SoepayEnvironment;
    organizeId: string;
    appId: string;
    terminalTid: string;
    active: boolean;
    apiKey?: string;
    apiToken?: string;
}

const LIMITS = { organizeId: 128, appId: 128, terminalTid: 64, apiKey: 256, apiToken: 512 } as const;

const PASSWORD_MANAGER_OPT_OUT = { 'data-1p-ignore': true, 'data-lpignore': 'true', 'data-form-type': 'other' } as const;

const P = 'resources.sites.soepayAccount';

export function emptySoepayForm(): SoepayAccountForm {
    return { environment: '', organizeId: '', appId: '', terminalTid: '', active: true, apiKey: '', apiToken: '' };
}

export function formFromAccount(account: SoepayAccount): SoepayAccountForm {
    return {
        environment: account.environment,
        organizeId: account.organizeId,
        appId: account.appId,
        terminalTid: account.terminalTid,
        active: account.active,
        apiKey: '',
        apiToken: '',
    };
}

export function soepayFormProblem(form: SoepayAccountForm, existing: SoepayAccount | null): string | null {
    if (form.environment === '') return 'environmentRequired';
    for (const field of ['organizeId', 'appId', 'terminalTid'] as const) {
        const value = form[field].trim();
        if (value === '') return `${field}Required`;
        if (value.length > LIMITS[field]) return `${field}TooLong`;
    }
    const environmentChanged = existing !== null && form.environment !== existing.environment;
    for (const field of ['apiKey', 'apiToken'] as const) {
        const value = form[field].trim();
        if (value.length > LIMITS[field]) return `${field}TooLong`;
        if (existing === null && value === '') return `${field}Required`;
        if (environmentChanged && value === '') return 'secretsRequiredForNewEnvironment';
    }
    return null;
}

export function soepayAccountBody(form: SoepayAccountForm): SoepayAccountBody {
    if (form.environment === '') throw new Error('a SoePay account needs its environment');
    const body: SoepayAccountBody = {
        environment: form.environment,
        organizeId: form.organizeId.trim(),
        appId: form.appId.trim(),
        terminalTid: form.terminalTid.trim(),
        active: form.active,
    };
    if (form.apiKey.trim() !== '') body.apiKey = form.apiKey.trim();
    if (form.apiToken.trim() !== '') body.apiToken = form.apiToken.trim();
    return body;
}

interface SiteSoepayAccountPanelProps {
    siteId: string;
}

export const SiteSoepayAccountPanel: React.FC<SiteSoepayAccountPanelProps> = ({ siteId }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const { permissions, isLoading: permissionsLoading } = usePermissions();
    const canManage = !permissionsLoading && hasPermission(permissions as string[] | null | undefined, 'system:admin:all');

    const accountUrl = `${API_CONFIG.endpoints.backend.sites}/${encodeURIComponent(siteId)}/soepay-account`;

    const [loading, setLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [account, setAccount] = useState<SoepayAccount | null>(null);
    const [form, setForm] = useState<SoepayAccountForm>(emptySoepayForm);
    const [problem, setProblem] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    const translateRef = useRef(translate);
    translateRef.current = translate;

    useEffect(() => {
        if (!canManage || !siteId) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const response = await apiClient.get<{ data: SoepayAccount }>(accountUrl);
                if (cancelled) return;
                setAccount(response.data);
                setForm(formFromAccount(response.data));
                setLoadFailed(false);
            } catch (error) {
                if (cancelled) return;
                if ((error as ApiError).errorCode === 'KIOSK_SOEPAY_ACCOUNT_NOT_FOUND') {
                    setAccount(null);
                    setForm(emptySoepayForm());
                    setLoadFailed(false);
                } else {
                    setLoadFailed(true);
                    notify(translateRef.current(`${P}.loadError`), { type: 'error' });
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [canManage, siteId, accountUrl, notify]);

    const update = useCallback(<K extends keyof SoepayAccountForm>(field: K, value: SoepayAccountForm[K]) => {
        setForm((current) => ({ ...current, [field]: value }));
        setProblem(null);
    }, []);

    if (!canManage) return null;

    const handleSave = async () => {
        const found = soepayFormProblem(form, account);
        if (found !== null) {
            setProblem(found);
            return;
        }
        setBusy(true);
        try {
            const response = await apiClient.put<{ data: SoepayAccount }>(accountUrl, soepayAccountBody(form));
            setAccount(response.data);
            setForm(formFromAccount(response.data));
            notify(translate(`${P}.saved`), { type: 'success' });
        } catch (error) {
            const apiError = error as ApiError;
            notify(apiError.errorCode === 'VALIDATION_FAILED' ? apiError.message : translate(`${P}.saveError`), { type: 'error' });
        } finally {
            setBusy(false);
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        if (event.key !== 'Enter' || event.nativeEvent.isComposing) return;
        const target = event.target as HTMLElement;
        if (target.tagName !== 'INPUT') return;
        event.preventDefault();
        const type = (target as HTMLInputElement).type;
        if (type !== 'text' && type !== 'password') return;
        if (!busy) void handleSave();
    };

    const environmentChanged = account !== null && form.environment !== '' && form.environment !== account.environment;

    const secretHelper = (set: boolean | undefined) => translate(set ? `${P}.secretKept` : `${P}.secretRequired`);

    const body = () => {
        if (loading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                </Box>
            );
        }
        if (loadFailed) {
            return (
                <Typography variant="body2" color="error">
                    {translate(`${P}.loadError`)}
                </Typography>
            );
        }
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }} onKeyDown={handleKeyDown}>
                <Alert severity="warning">{translate(`${P}.intro`)}</Alert>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                        size="small"
                        color={account === null ? 'default' : account.active ? 'success' : 'warning'}
                        label={translate(account === null ? `${P}.status.notSetUp` : account.active ? `${P}.status.active` : `${P}.status.inactive`)}
                    />
                    {account !== null && (
                        <Typography variant="caption" color="text.secondary">
                            {translate(`${P}.updated`, { at: new Date(account.updatedAt).toLocaleString(), by: account.updatedBy })}
                        </Typography>
                    )}
                </Box>
                <FormControl fullWidth>
                    <InputLabel id={`soepay-environment-${siteId}`}>{translate(`${P}.environment`)}</InputLabel>
                    <Select
                        labelId={`soepay-environment-${siteId}`}
                        label={translate(`${P}.environment`)}
                        value={form.environment}
                        onChange={(event: SelectChangeEvent) => update('environment', event.target.value as SoepayEnvironment)}
                        disabled={busy}
                    >
                        <MenuItem value="uat">{translate(`${P}.environments.uat`)}</MenuItem>
                        <MenuItem value="prod">{translate(`${P}.environments.prod`)}</MenuItem>
                    </Select>
                </FormControl>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                    <TextField label={translate(`${P}.organizeId`)} value={form.organizeId} onChange={(event) => update('organizeId', event.target.value)} inputProps={{ maxLength: LIMITS.organizeId }} disabled={busy} fullWidth />
                    <TextField label={translate(`${P}.appId`)} value={form.appId} onChange={(event) => update('appId', event.target.value)} inputProps={{ maxLength: LIMITS.appId }} disabled={busy} fullWidth />
                    <TextField label={translate(`${P}.terminalTid`)} value={form.terminalTid} onChange={(event) => update('terminalTid', event.target.value)} inputProps={{ maxLength: LIMITS.terminalTid }} disabled={busy} fullWidth />
                </Box>
                {environmentChanged && <Alert severity="warning">{translate(`${P}.environmentChanged`)}</Alert>}
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                    <TextField
                        label={translate(`${P}.apiKey`)}
                        type="password"
                        autoComplete="off"
                        value={form.apiKey}
                        onChange={(event) => update('apiKey', event.target.value)}
                        helperText={secretHelper(account?.apiKeySet)}
                        inputProps={{ maxLength: LIMITS.apiKey, 'data-testid': 'soepay-api-key', ...PASSWORD_MANAGER_OPT_OUT }}
                        disabled={busy}
                        fullWidth
                    />
                    <TextField
                        label={translate(`${P}.apiToken`)}
                        type="password"
                        autoComplete="off"
                        value={form.apiToken}
                        onChange={(event) => update('apiToken', event.target.value)}
                        helperText={secretHelper(account?.apiTokenSet)}
                        inputProps={{ maxLength: LIMITS.apiToken, 'data-testid': 'soepay-api-token', ...PASSWORD_MANAGER_OPT_OUT }}
                        disabled={busy}
                        fullWidth
                    />
                </Box>
                <FormControlLabel
                    control={<Switch checked={form.active} onChange={(event) => update('active', event.target.checked)} disabled={busy} />}
                    label={translate(`${P}.active`)}
                />
                <Typography variant="caption" color="text.secondary">
                    {translate(`${P}.activeHelper`)}
                </Typography>
                {problem !== null && (
                    <Typography variant="body2" color="error">
                        {translate(`${P}.problems.${problem}`)}
                    </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={busy}
                        sx={{ backgroundColor: '#00C853', '&:hover': { backgroundColor: '#00A843' }, textTransform: 'none', fontWeight: 600 }}
                    >
                        {translate(`${P}.save`)}
                    </Button>
                </Box>
            </Box>
        );
    };

    return (
        <Card sx={{ mb: 3, overflow: 'hidden' }}>
            <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <CreditCardIcon sx={{ mr: 2, color: '#00C853' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {translate(`${P}.title`)}
                    </Typography>
                </Box>
                {body()}
            </CardContent>
        </Card>
    );
};
